import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import Http from '../../../../core/Http';
import AuthorDisplay from '../../../../components/widgets/AuthorDisplay';
import CustomSelect from '../../../../components/widgets/CustomSelect';
import Pagination from '../../../../components/widgets/Pagination';
import Tabs from '../../../../components/widgets/Tabs';
import { mapAllianceInviteCandidateFromUserList } from '../alli/create/spaceCreateApi';
import { formatAllianceMemberRoleLabel } from '../alli/members/allianceMemberRoleLabels';
import { isGlowPlatformAdmin } from '../../../../assets/utils/comm/platformAdminUtils';

/** 이름(label)·로그인 아이디(userId)만 검색 (소속·역할 코드 제외) */
function candidateInviteMatchesSearch(c, needles) {
  if (!needles.length) return false;
  const name = c.label != null ? String(c.label).toLowerCase() : '';
  const userId = c.userId != null ? String(c.userId).toLowerCase() : '';
  const hay = `${name} ${userId}`;
  return needles.every((n) => hay.includes(n));
}

/** GLOW 플랫폼 관리자(ADMIN · SYSTEM) — 초대 후보 목록에서 제외 */
function isGlowPlatformAdminInviteCandidate(c) {
  return isGlowPlatformAdmin({
    userRoleCd: c?.userRoleCd ?? c?.user_role_cd,
  });
}

const INVITE_CANDIDATES_PAGE_SIZE = 5;
const INVITE_ROLE_CODES = ['PI', 'RESEARCHER', 'OBSERVER'];
const SIMPLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Alliance Space 초대 제안 팝업
 * config.spaces: { id, title }[] — 초대 제안자(현재 사용자)가 개설·참여 중 선택 가능한 스페이스
 * config.fixedSpace: { id, title } — 대시보드 등에서 현재 스페이스 고정 시 콤보 대신 읽기 전용 표시
 * config.dashboardMultiInvite + config.inviteCandidates — 우측에서 다중 선택 후 일괄 전송(레거시, 전체 목록)
 * config.inviteCandidatesSource — 서버 페이징 API(/api/alli/spaces/{id}/invite-candidates). 우선 적용.
 *   검색어 입력 후에만 목록 조회; 검색은 이름(USER_NM)·로그인 아이디(USER_ID) 기준.
 *   { spaceUid, linkSourceTypeCd?, linkSourceUid?, excludeUserId? }
 *   (항목: userUid, label, 선택적 userId·roleCd·orgHint·userRoleCd — userRoleCd=ADMIN 인 행은 목록에서 제외)
 * config.receiverName, config.receiverUid — 단일 초대 수신자
 * config.initialSpaceId — 미리 선택할 스페이스 id(문자열)
 * config.linkedTaskTitle / linkedTaskMinistry / linkedTaskAgency — 연결과제 정보 블록
 * 전송 시 onClose(true, { spaceUid, messageText }) 또는 다중 시
 * { spaceUid, messageText, receiverUids: string[], receiverRoleByUid?: Record<string, string> }
 */
const AllianceSpaceInviteProposalPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const fixedSpace = config?.fixedSpace && typeof config.fixedSpace === 'object' ? config.fixedSpace : null;
  const dashboardMultiInvite = config?.dashboardMultiInvite === true;
  const inviteCandidatesSource =
    config?.inviteCandidatesSource && typeof config.inviteCandidatesSource === 'object'
      ? config.inviteCandidatesSource
      : null;
  const useServerInviteCandidates =
    dashboardMultiInvite &&
    inviteCandidatesSource != null &&
    inviteCandidatesSource.spaceUid != null &&
    String(inviteCandidatesSource.spaceUid).trim() !== '';

  const inviteCandidates = useMemo(() => {
    const raw = Array.isArray(config?.inviteCandidates) ? config.inviteCandidates : [];
    return raw.filter((c) => !isGlowPlatformAdminInviteCandidate(c));
  }, [config?.inviteCandidates]);

  const spaces = useMemo(() => {
    const raw = Array.isArray(config?.spaces) ? config.spaces : [];
    if (fixedSpace?.id != null) {
      return [{ id: fixedSpace.id, title: fixedSpace.title || `Space #${fixedSpace.id}` }];
    }
    return raw;
  }, [config?.spaces, fixedSpace]);

  const spaceSelectOptions = useMemo(
    () =>
      spaces.map((s) => ({
        value: String(s.id),
        label: s.title || `Space #${s.id}`,
      })),
    [spaces]
  );

  const isBulkInvite = config?.bulkInvite === true;
  const receiverName = config?.receiverName || '';
  const receiverUid = config?.receiverUid;
  const bulkInviteMembers = useMemo(
    () => (Array.isArray(config?.bulkInviteMembers) ? config.bulkInviteMembers : []),
    [config?.bulkInviteMembers]
  );
  const linkedTaskTitle = (config?.linkedTaskTitle && String(config.linkedTaskTitle).trim()) || '';
  const linkedTaskMinistry = (config?.linkedTaskMinistry && String(config.linkedTaskMinistry).trim()) || '';
  const linkedTaskAgency = (config?.linkedTaskAgency && String(config.linkedTaskAgency).trim()) || '';

  const showLinkedProject = Boolean(linkedTaskTitle || linkedTaskMinistry || linkedTaskAgency);
  const agencyRowLabel = linkedTaskMinistry
    ? t('popup.allianceSpaceInvite.agencyLabelWithMinistry')
    : t('popup.allianceSpaceInvite.agencyLabelDemand');

  const initialSpace = fixedSpace?.id != null
    ? String(fixedSpace.id)
    : (config?.initialSpaceId || (spaces[0]?.id ?? ''));
  const [spaceId, setSpaceId] = useState(() => initialSpace);
  const [messageText, setMessageText] = useState(t('popup.allianceSpaceInvite.defaultGuide'));
  const [sendMode, setSendMode] = useState('PUSH');
  const [recipientInputTab, setRecipientInputTab] = useState('MEMBER');
  const [guestEmailInput, setGuestEmailInput] = useState('');
  const [guestEmails, setGuestEmails] = useState([]);
  const [memberCheckLoading, setMemberCheckLoading] = useState(false);
  /** 상위에서 온 joinStatusCd: INVITED | ACCEPTED | REJECTED | absent | error(조회 실패 시 전송 허용) */
  const [receiverJoinStatus, setReceiverJoinStatus] = useState(null);

  const [selectedInviteUids, setSelectedInviteUids] = useState(() => new Set());
  const [selectedInviteRoleMap, setSelectedInviteRoleMap] = useState(() => ({}));
  /** 서버 페이징 시 다른 페이지에 있던 선택 인원도 이름·아이디 표시 유지 */
  const [selectedInviteDisplayByUid, setSelectedInviteDisplayByUid] = useState(() => ({}));
  const [spaceBlockedUidsLoading, setSpaceBlockedUidsLoading] = useState(false);
  const [spaceBlockedUids, setSpaceBlockedUids] = useState(() => new Set());
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [inviteCandidatesPage, setInviteCandidatesPage] = useState(1);
  const [serverInviteRows, setServerInviteRows] = useState([]);
  const [serverInviteTotal, setServerInviteTotal] = useState(0);
  const [serverInviteTotalPages, setServerInviteTotalPages] = useState(1);
  const [serverInviteLoading, setServerInviteLoading] = useState(false);
  const [serverInvitePage, setServerInvitePage] = useState(1);
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
  const [roleLabelByCode, setRoleLabelByCode] = useState(() => ({
    PI: formatAllianceMemberRoleLabel('PI'),
    RESEARCHER: formatAllianceMemberRoleLabel('Researcher'),
    OBSERVER: formatAllianceMemberRoleLabel('Observer'),
  }));
  const candidateListRef = useRef(null);

  const candidateSearchNeedles = useMemo(() => {
    const t = candidateSearchQuery.trim().toLowerCase();
    if (!t) return [];
    return t.split(/\s+/).filter(Boolean);
  }, [candidateSearchQuery]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchKeyword(candidateSearchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [candidateSearchQuery]);

  useEffect(() => {
    setServerInvitePage(1);
  }, [debouncedSearchKeyword]);

  const filteredInviteCandidates = useMemo(() => {
    if (useServerInviteCandidates) return [];
    if (candidateSearchNeedles.length === 0) return [];
    return inviteCandidates.filter((c) => candidateInviteMatchesSearch(c, candidateSearchNeedles));
  }, [useServerInviteCandidates, inviteCandidates, candidateSearchNeedles]);

  const inviteCandidatesTotalPages = Math.max(
    1,
    Math.ceil(filteredInviteCandidates.length / INVITE_CANDIDATES_PAGE_SIZE)
  );
  const inviteCandidatesSafePage = Math.min(
    Math.max(1, inviteCandidatesPage),
    inviteCandidatesTotalPages
  );
  const pagedInviteCandidates = useMemo(() => {
    const start = (inviteCandidatesSafePage - 1) * INVITE_CANDIDATES_PAGE_SIZE;
    return filteredInviteCandidates.slice(start, start + INVITE_CANDIDATES_PAGE_SIZE);
  }, [filteredInviteCandidates, inviteCandidatesSafePage]);

  useEffect(() => {
    setInviteCandidatesPage((p) => Math.min(p, inviteCandidatesTotalPages));
  }, [inviteCandidatesTotalPages]);

  useEffect(() => {
    setInviteCandidatesPage(1);
  }, [candidateSearchQuery, inviteCandidates.length]);

  useEffect(() => {
    const el = candidateListRef.current;
    if (el) el.scrollTop = 0;
  }, [inviteCandidatesSafePage, serverInvitePage, useServerInviteCandidates]);

  useEffect(() => {
    let cancelled = false;
    if (!useServerInviteCandidates || !spaceId) {
      setServerInviteRows([]);
      setServerInviteTotal(0);
      setServerInviteTotalPages(1);
      setServerInviteLoading(false);
      return undefined;
    }
    if (!debouncedSearchKeyword.trim()) {
      setServerInviteRows([]);
      setServerInviteTotal(0);
      setServerInviteTotalPages(1);
      setServerInviteLoading(false);
      return undefined;
    }
    const src = inviteCandidatesSource;
    setServerInviteLoading(true);
    (async () => {
      try {
        const params = {
          pageNumber: serverInvitePage,
          size: INVITE_CANDIDATES_PAGE_SIZE,
        };
        if (debouncedSearchKeyword) params.searchKeyword = debouncedSearchKeyword;
        if (src?.linkSourceTypeCd) params.linkSourceTypeCd = src.linkSourceTypeCd;
        if (src?.linkSourceUid != null && Number(src.linkSourceUid) > 0) {
          params.linkSourceUid = src.linkSourceUid;
        }
        if (src?.excludeUserId) params.excludeUserId = src.excludeUserId;
        const res = await Http.get(
          `/api/alli/spaces/${encodeURIComponent(spaceId)}/invite-candidates`,
          { params }
        );
        if (cancelled) return;
        const list = res?.list ?? res?.content ?? [];
        const mapped = list
          .map((row) => mapAllianceInviteCandidateFromUserList(row))
          .filter(Boolean)
          .filter((c) => !isGlowPlatformAdminInviteCandidate(c));
        setServerInviteRows(mapped);
        setServerInviteTotal(Number(res?.totalElements ?? list.length) || 0);
        setServerInviteTotalPages(Math.max(1, Number(res?.totalPages) || 1));
      } catch {
        if (!cancelled) {
          setServerInviteRows([]);
          setServerInviteTotal(0);
          setServerInviteTotalPages(1);
        }
      } finally {
        if (!cancelled) setServerInviteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    useServerInviteCandidates,
    spaceId,
    serverInvitePage,
    debouncedSearchKeyword,
    inviteCandidatesSource,
  ]);

  useEffect(() => {
    if (!useServerInviteCandidates) return;
    setServerInvitePage((p) => Math.min(p, Math.max(1, serverInviteTotalPages)));
  }, [useServerInviteCandidates, serverInviteTotalPages]);

  useEffect(() => {
    let cancelled = false;
    if (!spaceId) return undefined;
    (async () => {
      try {
        const list = await Http.get(`/api/alli/spaces/${encodeURIComponent(spaceId)}/role-permissions`);
        if (cancelled || !Array.isArray(list)) return;
        const next = {
          PI: formatAllianceMemberRoleLabel('PI'),
          RESEARCHER: formatAllianceMemberRoleLabel('Researcher'),
          OBSERVER: formatAllianceMemberRoleLabel('Observer'),
        };
        list.forEach((row) => {
          const roleCd = String(row?.memberRoleCd || '').toUpperCase();
          if (!INVITE_ROLE_CODES.includes(roleCd)) return;
          const customName = String(row?.permissions?.meta?.roleName || '').trim();
          if (customName) next[roleCd] = customName;
        });
        setRoleLabelByCode(next);
      } catch {
        // 권한 라벨 조회 실패 시 기본 라벨 유지
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId]);

  useEffect(() => {
    if (!spaceId && spaces.length > 0) {
      setSpaceId(String(spaces[0].id));
    }
  }, [spaces, spaceId]);

  useEffect(() => {
    if (fixedSpace?.id != null) {
      setSpaceId(String(fixedSpace.id));
    }
  }, [fixedSpace?.id]);

  useEffect(() => {
    let cancelled = false;
    if (isBulkInvite || !spaceId || receiverUid == null || receiverUid === '' || dashboardMultiInvite) {
      setReceiverJoinStatus(null);
      setMemberCheckLoading(false);
      return;
    }

    setReceiverJoinStatus(null);
    setMemberCheckLoading(true);

    (async () => {
      try {
        const res = await Http.get(`/api/alli/members/space/${encodeURIComponent(spaceId)}`, {
          params: { page: 1, pageNumber: 1, size: 500 },
        });
        if (cancelled) return;
        const list = res?.list ?? res?.content ?? [];
        const m = list.find((x) => String(x.userUid) === String(receiverUid));
        if (!m) {
          setReceiverJoinStatus('absent');
        } else {
          const st = String(m.joinStatusCd || '').toUpperCase();
          const hasLeft = m.leftAt != null && String(m.leftAt).trim() !== '';
          if (hasLeft && (st === 'ACCEPTED' || st === 'INVITED')) {
            setReceiverJoinStatus('absent');
          } else {
            setReceiverJoinStatus(st || 'absent');
          }
        }
      } catch {
        if (!cancelled) setReceiverJoinStatus('error');
      } finally {
        if (!cancelled) setMemberCheckLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isBulkInvite, spaceId, receiverUid, dashboardMultiInvite]);

  /** 다중 초대: Space 기준 이미 초대·수락된 uid (후보 체크박스 비활성) — 서버 페이징 모드에서는 API에서 제외 */
  useEffect(() => {
    let cancelled = false;
    if (!dashboardMultiInvite || !spaceId) {
      setSpaceBlockedUids(new Set());
      setSpaceBlockedUidsLoading(false);
      return;
    }
    if (useServerInviteCandidates) {
      setSpaceBlockedUids(new Set());
      setSpaceBlockedUidsLoading(false);
      return;
    }
    setSpaceBlockedUidsLoading(true);
    (async () => {
      try {
        const res = await Http.get(`/api/alli/members/space/${encodeURIComponent(spaceId)}`, {
          params: { page: 1, pageNumber: 1, size: 500 },
        });
        if (cancelled) return;
        const list = res?.list ?? res?.content ?? [];
        const blocked = new Set();
        (list || []).forEach((m) => {
          const st = String(m?.joinStatusCd || '').toUpperCase();
          const hasLeft = m?.leftAt != null && String(m.leftAt).trim() !== '';
          if (!hasLeft && (st === 'ACCEPTED' || st === 'INVITED')) {
            blocked.add(String(m.userUid));
          }
        });
        setSpaceBlockedUids(blocked);
      } catch {
        if (!cancelled) setSpaceBlockedUids(new Set());
      } finally {
        if (!cancelled) setSpaceBlockedUidsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dashboardMultiInvite, spaceId, useServerInviteCandidates]);

  const alreadyMemberBlocked =
    receiverJoinStatus === 'INVITED' || receiverJoinStatus === 'ACCEPTED';

  const isEmailMode = sendMode === 'EMAIL';

  const selectedList = useMemo(() => {
    if (useServerInviteCandidates) {
      return [...selectedInviteUids].map((uid) => ({ userUid: uid }));
    }
    return inviteCandidates.filter((c) => selectedInviteUids.has(String(c.userUid)));
  }, [useServerInviteCandidates, inviteCandidates, selectedInviteUids]);

  const hasBlockedAmongSelected = useMemo(
    () =>
      !useServerInviteCandidates &&
      selectedList.some((c) => spaceBlockedUids.has(String(c.userUid))),
    [useServerInviteCandidates, selectedList, spaceBlockedUids]
  );

  /** 조회 완료 후에만 전송 가능(null·로딩 중 비활성). API 오류(error)는 전송 허용 */
  const canSubmitEffective = dashboardMultiInvite
    ? spaces.length > 0 &&
      Boolean(spaceId) &&
      (selectedInviteUids.size > 0 || (isEmailMode && guestEmails.length > 0)) &&
      (isEmailMode || selectedInviteUids.size > 0) &&
      !spaceBlockedUidsLoading &&
      !hasBlockedAmongSelected
    : isBulkInvite
      ? spaces.length > 0 && Boolean(spaceId)
      : spaces.length > 0 &&
        Boolean(spaceId) &&
        !memberCheckLoading &&
        !alreadyMemberBlocked &&
        receiverJoinStatus !== null;

  const toggleCandidate = useCallback((candidate) => {
    const uid = candidate && typeof candidate === 'object' && candidate.userUid != null ? candidate.userUid : candidate;
    const key = String(uid);
    if (spaceBlockedUids.has(key)) return;
    setSelectedInviteUids((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setSelectedInviteRoleMap((prevMap) => {
          if (!(key in prevMap)) return prevMap;
          const clone = { ...prevMap };
          delete clone[key];
          return clone;
        });
        setSelectedInviteDisplayByUid((prevDisp) => {
          if (!(key in prevDisp)) return prevDisp;
          const clone = { ...prevDisp };
          delete clone[key];
          return clone;
        });
      } else {
        next.add(key);
        setSelectedInviteRoleMap((prevMap) => ({ ...prevMap, [key]: prevMap[key] || 'RESEARCHER' }));
        const labelFromRow =
          candidate && typeof candidate === 'object' && String(candidate.label || '').trim()
            ? String(candidate.label).trim()
            : '';
        const label = labelFromRow || key;
        const userId =
          candidate && typeof candidate === 'object' && candidate.userId != null ? String(candidate.userId) : '';
        setSelectedInviteDisplayByUid((prevDisp) => ({
          ...prevDisp,
          [key]: { label, userId },
        }));
      }
      return next;
    });
  }, [spaceBlockedUids]);

  const handleChangeInviteRole = useCallback((uid, roleCd) => {
    const key = String(uid);
    const nextRole = String(roleCd || '').toUpperCase();
    if (!INVITE_ROLE_CODES.includes(nextRole)) return;
    setSelectedInviteRoleMap((prev) => ({ ...prev, [key]: nextRole }));
  }, []);

  useEffect(() => {
    if (!isEmailMode) {
      setRecipientInputTab('MEMBER');
    }
  }, [isEmailMode]);

  const handleAddGuestEmail = useCallback(() => {
    const normalized = String(guestEmailInput || '').trim().toLowerCase();
    if (!normalized) return;
    if (!SIMPLE_EMAIL_RE.test(normalized)) {
      window.alert(t('popup.allianceSpaceInvite.alertInvalidEmail'));
      return;
    }
    setGuestEmails((prev) => {
      if (prev.includes(normalized)) return prev;
      return [...prev, normalized];
    });
    setGuestEmailInput('');
  }, [guestEmailInput, t]);

  const removeGuestEmail = useCallback((email) => {
    const target = String(email || '').trim().toLowerCase();
    if (!target) return;
    setGuestEmails((prev) => prev.filter((it) => it !== target));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!canSubmitEffective || memberCheckLoading || !spaceId) return;
    if (dashboardMultiInvite) {
      const receiverUids = [...selectedInviteUids].filter((uid) => !spaceBlockedUids.has(String(uid)));
      const memberRecipients = receiverUids.map((uid) => ({ memberUid: Number(uid) }));
      const guestRecipients = isEmailMode
        ? guestEmails.map((email) => ({ guestEmail: email }))
        : [];
      const recipients = [...memberRecipients, ...guestRecipients];
      if (recipients.length === 0) return;
      const receiverRoleByUid = {};
      receiverUids.forEach((uid) => {
        const roleCd = String(selectedInviteRoleMap[String(uid)] || 'RESEARCHER').toUpperCase();
        receiverRoleByUid[String(uid)] = INVITE_ROLE_CODES.includes(roleCd) ? roleCd : 'RESEARCHER';
      });
      onClose(true, {
        spaceUid: spaceId,
        messageText: (messageText || '').trim(),
        sendMode,
        notifyPlatform: sendMode !== 'EMAIL',
        notifyEmail: sendMode === 'EMAIL',
        recipients,
        receiverUids,
        guestEmails,
        receiverRoleByUid,
      });
      return;
    }
    if (alreadyMemberBlocked) return;
    onClose(true, {
      spaceUid: spaceId,
      messageText: (messageText || '').trim(),
      sendMode,
      notifyPlatform: sendMode !== 'EMAIL',
      notifyEmail: sendMode === 'EMAIL',
    });
  }, [
    alreadyMemberBlocked,
    canSubmitEffective,
    dashboardMultiInvite,
    guestEmails,
    isEmailMode,
    memberCheckLoading,
    messageText,
    onClose,
    sendMode,
    selectedInviteUids,
    selectedInviteRoleMap,
    spaceBlockedUids,
    spaceId,
  ]);

  const submitButtonLabel = () => {
    if (dashboardMultiInvite) {
      if (spaceBlockedUidsLoading) return '확인 중…';
      if (
        useServerInviteCandidates &&
        debouncedSearchKeyword.trim() &&
        serverInviteLoading &&
        selectedInviteUids.size === 0 &&
        guestEmails.length === 0
      ) {
        return t('popup.allianceSpaceInvite.loadingList');
      }
      if (selectedInviteUids.size === 0 && (!isEmailMode || guestEmails.length === 0)) {
        return isEmailMode ? t('popup.allianceSpaceInvite.addRecipientFirst') : t('popup.allianceSpaceInvite.selectInviteeFirst');
      }
      if (hasBlockedAmongSelected) return t('popup.allianceSpaceInvite.excludeAlreadyInSpace');
      return t('popup.allianceSpaceInvite.sendInviteProposalCount', { count: selectedInviteUids.size + (isEmailMode ? guestEmails.length : 0) });
    }
    if (memberCheckLoading) return t('popup.allianceSpaceInvite.checking');
    if (receiverJoinStatus === 'ACCEPTED') return t('popup.allianceSpaceInvite.inviteCompleted');
    if (receiverJoinStatus === 'INVITED') return t('popup.allianceSpaceInvite.invited');
    return isBulkInvite ? t('popup.allianceSpaceInvite.bulkSendInviteProposal') : t('popup.allianceSpaceInvite.sendInviteProposal');
  };

  const showRecipientInputTabs = isEmailMode;
  const showMemberCandidatePanel = !showRecipientInputTabs || recipientInputTab === 'MEMBER';
  const showCandidateSearch = dashboardMultiInvite && showMemberCandidatePanel;
  const hasInviteSearchQuery = useServerInviteCandidates
    ? Boolean(debouncedSearchKeyword.trim())
    : candidateSearchNeedles.length > 0;
  const displayRows = useServerInviteCandidates ? serverInviteRows : pagedInviteCandidates;
  const totalCountForSummary = useServerInviteCandidates ? serverInviteTotal : filteredInviteCandidates.length;
  const totalPagesForPagination = useServerInviteCandidates
    ? Math.max(1, serverInviteTotalPages)
    : inviteCandidatesTotalPages;
  const safePageOneBased = useServerInviteCandidates
    ? Math.min(Math.max(1, serverInvitePage), totalPagesForPagination)
    : inviteCandidatesSafePage;
  const leftColumn = (
    <>
      {isBulkInvite && bulkInviteMembers.length > 0 ? (
        <div className="alliance-space-invite-field">
          <span className="alliance-space-invite-label">{t('popup.allianceSpaceInvite.bulkTargets')}</span>
          <div className="alliance-space-invite-bulk-members">
            <div className="alliance-space-invite-bulk-summary">
              {t('popup.allianceSpaceInvite.totalCount', { count: bulkInviteMembers.length })}
            </div>
            <div className="alliance-space-invite-bulk-list">
              {bulkInviteMembers.map((member) => (
                <span key={String(member.id ?? member.name)} className="alliance-space-invite-bulk-chip">
                  {member.name || '-'}{member.type ? ` (${member.type})` : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="alliance-space-invite-field">
        {fixedSpace?.id != null ? (
          <>
            <span className="alliance-space-invite-label">{t('popup.allianceSpaceInvite.currentSpaceLabel')}</span>
            <div className="alliance-space-invite-fixed-space">
              <strong>{fixedSpace.title || `Space #${fixedSpace.id}`}</strong>
            </div>
          </>
        ) : (
          <>
            <label className="alliance-space-invite-label" htmlFor="alliance-space-invite-select">
              {t('popup.allianceSpaceInvite.selectSpaceLabel')}
            </label>
            {spaces.length === 0 ? (
              <p className="alliance-space-invite-empty text-muted small mb-0">
                {t('popup.allianceSpaceInvite.emptySpacesHint')}
              </p>
            ) : (
              <div className="alliance-space-invite-select-wrap">
                <CustomSelect
                  inputId="alliance-space-invite-select"
                  classNamePrefix="alliance-space-invite-select"
                  value={spaceId || ''}
                  options={spaceSelectOptions}
                  onChange={(next) => setSpaceId(next != null && next !== '' ? String(next) : '')}
                  placeholder={t('popup.allianceSpaceInvite.selectSpacePlaceholder')}
                  isClearable={false}
                  isSearchable={spaces.length > 1}
                />
              </div>
            )}
          </>
        )}
      </div>

      {!dashboardMultiInvite && alreadyMemberBlocked && (
        <p className="alliance-space-invite-member-note mb-0" role="status">
          {t('popup.allianceSpaceInvite.alreadyInSpace', { name: receiverName || t('popup.allianceSpaceInvite.recipientFallback') })}{' '}
          <span className="alliance-space-invite-member-badge">
            {receiverJoinStatus === 'ACCEPTED' ? t('popup.allianceSpaceInvite.inviteCompleted') : t('popup.allianceSpaceInvite.invited')}
          </span>
        </p>
      )}

      {showLinkedProject ? (
        <div className="alliance-space-invite-field">
          <span className="alliance-space-invite-label">{t('popup.allianceSpaceInvite.linkedTaskInfo')}</span>
          <div className="alliance-space-invite-linked-project">
            {linkedTaskTitle ? (
              <div className="alliance-space-invite-linked-row">
                <span className="alliance-space-invite-linked-key">{t('popup.allianceSpaceInvite.taskNameKey')}</span>
                <span className="alliance-space-invite-linked-val">{linkedTaskTitle}</span>
              </div>
            ) : null}
            {linkedTaskMinistry ? (
              <div className="alliance-space-invite-linked-row">
                <span className="alliance-space-invite-linked-key">{t('popup.allianceSpaceInvite.ministryKey')}</span>
                <span className="alliance-space-invite-linked-val">{linkedTaskMinistry}</span>
              </div>
            ) : null}
            {linkedTaskAgency ? (
              <div className="alliance-space-invite-linked-row">
                <span className="alliance-space-invite-linked-key">{agencyRowLabel}</span>
                <span className="alliance-space-invite-linked-val">{linkedTaskAgency}</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="alliance-space-invite-field">
        <label className="alliance-space-invite-label" htmlFor="alliance-space-invite-message">
          {t('popup.allianceSpaceInvite.guideMessageLabel')}
        </label>
        <textarea
          id="alliance-space-invite-message"
          className={`form-control alliance-space-invite-textarea ${!dashboardMultiInvite && (alreadyMemberBlocked || memberCheckLoading) ? 'alliance-space-invite-textarea--readonly' : ''}`}
          rows={5}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder={t('popup.allianceSpaceInvite.guideMessagePlaceholder')}
          maxLength={2000}
          readOnly={!dashboardMultiInvite && (alreadyMemberBlocked || memberCheckLoading)}
        />
      </div>
    </>
  );

  const rightColumn = dashboardMultiInvite ? (
    <div className="alliance-space-invite-candidates" style={{ minHeight: '383px' }}>
      <div className="alliance-space-invite-candidates-head">
        <span className="alliance-space-invite-label alliance-space-invite-candidates-title mb-0">
          {t('popup.allianceSpaceInvite.invitableParticipants')}
        </span>
      </div>
      {showRecipientInputTabs ? (
        <div className="alliance-space-invite-input-tabs">
          <Tabs
            tabs={[t('popup.allianceSpaceInvite.memberSearchTab'), t('popup.allianceSpaceInvite.emailInputTab')]}
            activeTab={recipientInputTab === 'GUEST' ? 1 : 0}
            onChange={(tabIndex) => setRecipientInputTab(tabIndex === 1 ? 'GUEST' : 'MEMBER')}
            variant="segmented"
            className="alliance-space-invite-recipient-tabs-widget"
          />
        </div>
      ) : null}
      {showCandidateSearch ? (
        <div className="alliance-space-invite-candidates-search">
          <label className="visually-hidden" htmlFor="alliance-invite-candidate-search">
            {t('popup.allianceSpaceInvite.searchParticipantLabel')}
          </label>
          <input
            id="alliance-invite-candidate-search"
            type="search"
            className="form-control form-control-sm alliance-space-invite-candidates-search-input"
            placeholder={t('popup.allianceSpaceInvite.searchParticipantPlaceholder')}
            value={candidateSearchQuery}
            onChange={(e) => setCandidateSearchQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      ) : null}
      {!showMemberCandidatePanel ? (
        <div className="alliance-space-invite-guest-input-wrap">
          <label className="alliance-space-invite-label" htmlFor="alliance-guest-email-input">{t('popup.allianceSpaceInvite.emailAddressLabel')}</label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="alliance-guest-email-input"
              type="email"
              className="form-control form-control-sm alliance-space-invite-guest-email-input"
              placeholder="example@email.com"
              value={guestEmailInput}
              onChange={(e) => setGuestEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddGuestEmail();
                }
              }}
            />
            <button type="button" className="btn btn-sm alliance-space-invite-guest-add-btn" onClick={handleAddGuestEmail} aria-label={t('popup.allianceSpaceInvite.addEmailAria')}>+</button>
          </div>
          <p className="small text-muted mt-2 mb-0">{t('popup.allianceSpaceInvite.guestEmailHint')}</p>
        </div>
      ) : spaceBlockedUidsLoading ? (
        <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.checkingMemberList')}</p>
      ) : !useServerInviteCandidates && inviteCandidates.length === 0 ? (
        <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.emptyList')}</p>
      ) : !hasInviteSearchQuery ? (
        <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.searchHint')}</p>
      ) : useServerInviteCandidates && serverInviteLoading && displayRows.length === 0 ? (
        <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.loadingList')}</p>
      ) : useServerInviteCandidates && !serverInviteLoading && serverInviteTotal === 0 ? (
        <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.noSearchResult')}</p>
      ) : !useServerInviteCandidates && filteredInviteCandidates.length === 0 ? (
        <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.noSearchResult')}</p>
      ) : (
        <>
        <ul ref={candidateListRef} className="alliance-space-invite-candidates-list">
          {displayRows.map((c) => {
            const key = String(c.userUid);
            const blocked = spaceBlockedUids.has(key);
            const checked = selectedInviteUids.has(key);
            const orgLine = (c.orgHint && String(c.orgHint).trim()) || '';
            return (
              <li key={key}>
                <label className={`alliance-space-invite-candidate-row${blocked ? ' is-blocked' : ''}`}>
                  <input
                    type="checkbox"
                    className="alliance-space-invite-candidate-check"
                    checked={checked}
                    disabled={blocked}
                    onChange={() => toggleCandidate(c)}
                    aria-label={t('popup.allianceSpaceInvite.selectInviteAria', { name: c.label || key })}
                  />
                  <div className="alliance-space-invite-candidate-main">
                    <AuthorDisplay
                      regUid={c.userUid}
                      name={c.label || key}
                      userId={c.userId || ''}
                      authorCd={c.roleCd || ''}
                      org={orgLine}
                      className="alliance-space-invite-candidate-author"
                    />
                    {blocked ? (
                      <span className="alliance-space-invite-candidate-badge">{t('popup.allianceSpaceInvite.alreadyInvitedOrJoined')}</span>
                    ) : null}
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
        <div className="alliance-space-invite-candidates-pagination-wrap">
          {totalPagesForPagination > 1 ? (
            <>
              <div className="alliance-space-invite-pagination-widget">
                <Pagination
                  currentPage={safePageOneBased - 1}
                  totalPages={totalPagesForPagination}
                  onPageChange={(p) =>
                    useServerInviteCandidates
                      ? setServerInvitePage(p + 1)
                      : setInviteCandidatesPage(p + 1)
                  }
                />
              </div>
              <p className="alliance-space-invite-page-summary small text-muted text-center mb-0 mt-1">
                {t('popup.allianceSpaceInvite.pageSummary', { total: totalCountForSummary, page: safePageOneBased, pages: totalPagesForPagination })}
              </p>
            </>
          ) : (
            <p className="alliance-space-invite-page-summary small text-muted text-center mb-0">
              {t('popup.allianceSpaceInvite.totalCount', { count: totalCountForSummary })}
            </p>
          )}
        </div>
        {selectedInviteUids.size > 0 ? (
          <div className="alliance-space-invite-role-assign-wrap">
            <p className="alliance-space-invite-label mb-2">{t('popup.allianceSpaceInvite.assignRoleTitle')}</p>
            <ul className="alliance-space-invite-role-assign-list">
              {[...selectedInviteUids].map((uid) => {
                const key = String(uid);
                const snap = selectedInviteDisplayByUid[key];
                const person = snap
                  ? { userUid: key, label: snap.label, userId: snap.userId }
                  : displayRows.find((r) => String(r.userUid) === key)
                    || serverInviteRows.find((r) => String(r.userUid) === key)
                    || inviteCandidates.find((r) => String(r.userUid) === key)
                    || { userUid: key, label: key, userId: '' };
                const selectedRoleCd = selectedInviteRoleMap[key] || 'RESEARCHER';
                return (
                  <li key={`assign-${key}`} className="alliance-space-invite-role-assign-item">
                    <span className="alliance-space-invite-role-assign-name">
                      {(person.label || key)}{person.userId ? `(${person.userId})` : ''}
                    </span>
                    <select
                      className="form-select form-select-sm alliance-space-invite-role-assign-select"
                      value={selectedRoleCd}
                      onChange={(e) => handleChangeInviteRole(key, e.target.value)}
                    >
                      {INVITE_ROLE_CODES.map((code) => (
                        <option key={code} value={code}>{roleLabelByCode[code] || code}</option>
                      ))}
                    </select>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        </>
      )}
      <div className="alliance-space-invite-recipient-summary mt-3">
        <p className="alliance-space-invite-label mb-2">{t('popup.allianceSpaceInvite.toLabel')}</p>
        {selectedInviteUids.size === 0 && guestEmails.length === 0 ? (
          <p className="small text-muted mb-0">{t('popup.allianceSpaceInvite.addRecipientFirst')}</p>
        ) : (
          <div className="alliance-space-invite-recipient-chips">
            {[...selectedInviteUids].map((uid) => {
              const key = String(uid);
              const snap = selectedInviteDisplayByUid[key];
              const name = snap?.label || key;
              const userId = snap?.userId ? `(${snap.userId})` : '';
              return (
                <span key={`recipient-member-${key}`} className="alliance-space-invite-recipient-chip">
                  {name}{userId}
                </span>
              );
            })}
            {guestEmails.map((email) => (
              <button
                key={`recipient-guest-${email}`}
                type="button"
                className="alliance-space-invite-recipient-chip alliance-space-invite-recipient-chip--guest"
                onClick={() => removeGuestEmail(email)}
                title={t('popup.allianceSpaceInvite.clickToRemove')}
              >
                {email} ×
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <PopupWrapper
      config={config}
      onClose={() => onClose(false)}
      displayType={config?.displayType || 'modal'}
      className="popup-alliance-space-invite"
    >
      <div className={`main-popup-body alliance-space-invite-body${dashboardMultiInvite ? ' alliance-space-invite-body--split' : ''}`}>
        <div className="alliance-space-invite-send-mode-tabs-wrap">
          <Tabs
            tabs={[t('popup.allianceSpaceInvite.pushTab'), t('popup.allianceSpaceInvite.emailTab')]}
            activeTab={sendMode === 'EMAIL' ? 1 : 0}
            onChange={(tabIndex) => setSendMode(tabIndex === 1 ? 'EMAIL' : 'PUSH')}
            variant="pill"
            className="alliance-space-invite-send-mode-tabs"
            activeColor="#111827"
          />
        </div>
        {dashboardMultiInvite ? (
          <div className="row g-3 g-lg-4 align-items-start alliance-space-invite-split">
            <div className="col-12 col-lg-7 alliance-space-invite-split-left">{leftColumn}</div>
            <div className="col-12 col-lg-5 alliance-space-invite-split-right">{rightColumn}</div>
          </div>
        ) : (
          leftColumn
        )}
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        <button
          type="button"
          className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`}
          onClick={() => onClose(false)}
        >
          {t('popup.common.cancel')}
        </button>
        <button
          type="button"
          className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`}
          onClick={handleSubmit}
          disabled={!canSubmitEffective}
        >
          {submitButtonLabel()}
        </button>
      </div>
      <style>{`
        .alliance-space-invite-body { padding-top: 4px; }
        .alliance-space-invite-body--split { padding-left: 0; padding-right: 0; }
        .alliance-space-invite-split-left { min-width: 0; }
        @media (min-width: 992px) {
          .alliance-space-invite-split-right {
            border-left: 1px solid #e2e8f0;
            padding-left: 1rem;
            min-height: 200px;
          }
        }
        @media (max-width: 991.98px) {
          .alliance-space-invite-split-right {
            border-top: 1px solid #e2e8f0;
            padding-top: 1rem;
          }
        }
        .alliance-space-invite-fixed-space {
          font-size: 15px;
          color: #0f172a;
          padding: 12px 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          line-height: 1.4;
        }
        .alliance-space-invite-candidates-search {
          flex-shrink: 0;
          margin-bottom: 8px;
        }
        .alliance-space-invite-candidates-search-input {
          font-size: 13px;
        }
        .alliance-space-invite-candidates-head {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }
        .alliance-space-invite-candidates-title {
          margin-bottom: 0 !important;
          flex: 1;
          min-width: 0;
        }
        .alliance-space-invite-send-mode-tabs-wrap {
          width: 100%;
          margin-bottom: 12px;
        }
        .alliance-space-invite-send-mode-tabs.tabs-container--pill {
          width: 100%;
          margin-bottom: 0;
        }
        .alliance-space-invite-input-tabs {
          width: 100%;
          margin-bottom: 10px;
        }
        .alliance-space-invite-recipient-tabs-widget.tabs-container--segmented {
          width: 100%;
          align-self: stretch;
          justify-content: center;
          margin-bottom: 0;
        }
        .alliance-space-invite-guest-input-wrap {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px;
          background: #f8fafc;
        }
        .alliance-space-invite-guest-email-input {
          flex: 1;
        }
        .alliance-space-invite-guest-add-btn {
          width: 38px;
          min-width: 38px;
          padding: 0;
          border: 1px solid #111827;
          border-radius: 8px;
          background: #111827;
          color: #fff;
          font-size: 22px;
          line-height: 1;
          font-weight: 500;
        }
        .alliance-space-invite-guest-add-btn:hover {
          background: #000;
          border-color: #000;
          color: #fff;
        }
        .alliance-space-invite-candidates-list {
          list-style: none;
          margin: 0;
          padding: 0;
          max-height: min(380px, 70vh);
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #fff;
        }
        .alliance-space-invite-candidates-pagination-wrap {
          flex-shrink: 0;
          margin-top: 8px;
          padding-bottom: 0;
        }
        .alliance-space-invite-pagination-widget {
          width: 100%;
        }
        .alliance-space-invite-page-summary {
          line-height: 1.3;
        }
        .alliance-space-invite-role-assign-wrap {
          margin-top: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
          padding: 10px;
        }
        .alliance-space-invite-role-assign-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 160px;
          overflow-y: auto;
        }
        .alliance-space-invite-role-assign-item {
          display: grid;
          grid-template-columns: 1fr 170px;
          gap: 8px;
          align-items: center;
        }
        .alliance-space-invite-role-assign-name {
          font-size: 13px;
          color: #0f172a;
          font-weight: 600;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .alliance-space-invite-role-assign-select {
          font-size: 12px;
          height: 32px;
        }
        .alliance-space-invite-recipient-summary {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #fff;
          padding: 10px;
        }
        .alliance-space-invite-recipient-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .alliance-space-invite-recipient-chip {
          display: inline-flex;
          align-items: center;
          border: 1px solid #cbd5e1;
          border-radius: 999px;
          background: #f8fafc;
          color: #334155;
          font-size: 12px;
          padding: 4px 10px;
        }
        .alliance-space-invite-recipient-chip--guest {
          border: 1px solid #f59e0b;
          background: #fff7ed;
          color: #9a3412;
          cursor: pointer;
        }
        .alliance-space-invite-candidate-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin: 0;
          padding: 8px 10px;
          border: none;
          border-bottom: 1px solid #eef2f7;
          border-radius: 0;
          cursor: pointer;
          background: transparent;
          font-size: 13px;
        }
        .alliance-space-invite-candidate-row:last-child {
          border-bottom: none;
        }
        .alliance-space-invite-candidate-row.is-blocked {
          opacity: 0.65;
          cursor: not-allowed;
          background: #f8fafc;
        }
        .alliance-space-invite-candidate-check {
          flex-shrink: 0;
          margin: 6px 0 0 0;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        .alliance-space-invite-candidate-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .alliance-space-invite-candidate-author.author-display {
          min-width: 0;
          width: 100%;
        }
        .alliance-space-invite-candidate-author .alli-author-name {
          font-weight: 600;
          color: #0f172a;
        }
        .alliance-space-invite-candidate-badge {
          font-size: 11px;
          color: #64748b;
        }
        .alliance-space-invite-intro { font-size: 14px; color: #334155; margin-bottom: 16px; }
        .alliance-space-invite-field { margin-bottom: 14px; }
        .alliance-space-invite-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
        .alliance-space-invite-select-wrap {
          width: 100%;
          font-size: 14px;
        }
        .alliance-space-invite-textarea { font-size: 14px; resize: vertical; min-height: 120px; }
        .alliance-space-invite-textarea--readonly {
          background-color: #f1f5f9;
          color: #64748b;
          cursor: not-allowed;
        }
        .alliance-space-invite-empty { line-height: 1.45; }
        .alliance-space-invite-member-note {
          font-size: 13px;
          color: #475569;
          padding: 10px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          margin-bottom: 14px;
        }
        .alliance-space-invite-member-badge {
          display: inline-block;
          margin-left: 4px;
          padding: 2px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          background: #e2e8f0;
          color: #334155;
        }
        .alliance-space-invite-bulk-members {
          padding: 10px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
        }
        .alliance-space-invite-bulk-summary {
          font-size: 13px;
          color: #334155;
          margin-bottom: 8px;
        }
        .alliance-space-invite-bulk-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .alliance-space-invite-bulk-chip {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 999px;
          background: #e2e8f0;
          color: #334155;
          font-size: 12px;
          font-weight: 500;
        }
        .alliance-space-invite-linked-project {
          font-size: 13px;
          color: #0f172a;
          padding: 10px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          line-height: 1.45;
          word-break: break-word;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .alliance-space-invite-linked-row {
          display: grid;
          grid-template-columns: 92px 1fr;
          gap: 8px 12px;
          align-items: start;
        }
        .alliance-space-invite-linked-key {
          font-weight: 600;
          color: #64748b;
          font-size: 12px;
        }
        .alliance-space-invite-linked-val {
          color: #0f172a;
          font-size: 14px;
        }
      `}</style>
    </PopupWrapper>
  );
};

export default AllianceSpaceInviteProposalPopup;
